import { showToast } from './toast'
import { logger } from './logger'

const DEFAULT_ERROR_MESSAGE = 'Something went wrong'
const NETWORK_ERROR_MESSAGE = 'Network error — please check your connection and try again'

type CallApiNetworkMarkedError = Error & { fromCallApiNetwork?: boolean }

export function markCallApiNetworkError(err: Error): Error {
  const tagged = err as CallApiNetworkMarkedError

  tagged.fromCallApiNetwork = true

  return err
}

const isNetworkFailure = (err: unknown): boolean => {
  if (err && typeof err === 'object' && (err as CallApiNetworkMarkedError).fromCallApiNetwork) {
    return true
  }

  if (!(err instanceof TypeError)) {
    return false
  }

  return /failed to fetch|load failed|networkerror|network request failed/i.test(err.message || '')
}

export const getApiErrorMessage = (err: any, fallback: string): string => {
  if (isNetworkFailure(err)) {
    return NETWORK_ERROR_MESSAGE
  }

  const payload = err?.payload

  if (typeof payload === 'string') {
    return removeFieldPrefix(payload) || fallback
  }

  const message = payload?.message

  if (!message) {
    return fallback
  }

  if (typeof message === 'string') {
    return removeFieldPrefix(message)
  }

  if (Array.isArray(message)) {
    return formatApiErrorMessage(message)
  }

  return fallback
}

const removeFieldPrefix = (msg: string): string => msg.replace(/^(?:[a-z][a-zA-Z0-9]*\.)+/, '')

export const formatApiErrorMessage = (message: string | string[] | undefined): string => {
  if (!message) {
    return DEFAULT_ERROR_MESSAGE
  }

  if (typeof message === 'string') {
    return removeFieldPrefix(message) || DEFAULT_ERROR_MESSAGE
  }

  if (!Array.isArray(message)) {
    return DEFAULT_ERROR_MESSAGE
  }

  const formattedMessages = message
    .filter((msg): msg is string => typeof msg === 'string')
    .map(removeFieldPrefix)

  return formattedMessages.length > 0 ? formattedMessages.join(', ') : DEFAULT_ERROR_MESSAGE
}

export const handleApiError = (err: any, context?: string) => {
  const rawPayload = err?.payload
  const errorMessage =
    (typeof rawPayload === 'string' ? rawPayload : rawPayload?.message) ||
    err?.message ||
    'Unknown error'
  const status = err?.status || err?.statusCode

  logger.error(context ? `${context}: ${errorMessage}` : `API Error: ${errorMessage}`, err, {
    status,
    payload: err?.payload,
    context,
  })

  if (Array.isArray(err)) {
    err.slice(0, 3).forEach((item: any) => {
      const message =
        typeof item === 'object' ? DEFAULT_ERROR_MESSAGE : removeFieldPrefix(String(item))

      showToast(message, {
        variant: 'error',
        autoHideDuration: 6000,
      })
    })
  } else {
    const displayMessage =
      typeof err === 'string' ? removeFieldPrefix(err) : formatApiErrorMessage(errorMessage)

    showToast(displayMessage || DEFAULT_ERROR_MESSAGE, {
      variant: 'error',
      autoHideDuration: 6000,
    })
  }
}
